import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import { Icon } from '@iconify/react';
import ChangePasswordModal from '../components/Models/ChangePasswordModal';

export default function Profile(): React.ReactElement {
    const { user } = useSelector((state: RootState) => ({ user: state.user }));
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    const getUserInitials = (name: string) => {
        return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
    };

    const formatRole = (role: string) => {
        return role.split(/(?=[A-Z])/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl shadow-lg p-6 mb-6 text-white">
                <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border-2 border-white/30">
                        <span className="text-white font-bold text-2xl">
                            {user?.name ? getUserInitials(user.name) : 'U'}
                        </span>
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold mb-2 text-white">{user?.name || 'User'}</h1>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="bg-white/25 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-semibold text-white border border-white/20">
                                {formatRole(user?.role || 'user')}
                            </span>
                            <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-2.5 py-1.5 rounded-full border border-white/20">
                                <div className={`w-2.5 h-2.5 rounded-full ${user?.isActive ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                <span className={`text-xs font-semibold ${user?.isActive ? 'text-green-200' : 'text-red-200'}`}>
                                    {user?.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        <p className="text-white/90 text-sm font-medium">Willow ID: {user?.willowId}</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowChangePasswordModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-lg transition-all duration-200 border border-white/30"
                        >
                            <Icon icon="mdi:lock-reset" className="text-lg" />
                            <span className="text-sm font-medium">Change Password</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Profile Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Personal Information Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#7c3aed'}}>
                            <Icon icon="mdi:account" className="text-2xl" style={{color: 'white'}} />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Personal Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:account-circle" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Full Name</p>
                                <p className="font-medium text-gray-900">{user?.name || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:email" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium text-gray-900">{user?.email || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:phone" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Phone Number</p>
                                <p className="font-medium text-gray-900">{user?.phoneNumber || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Company Information Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#16a34a'}}>
                            <Icon icon="mdi:office-building" className="text-2xl" style={{color: 'white'}} />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Company Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:domain" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Company Name</p>
                                <p className="font-medium text-gray-900">{user?.companyName || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-start gap-3">
                                <Icon icon="mdi:map-marker" className="text-xl mt-1" style={{color: '#6b7280'}} />
                                <div className="flex-1">
                                    <p className="text-sm text-gray-500 mb-1">Company Address</p>
                                    <p className="font-medium text-gray-900 leading-relaxed">{user?.companyAddress || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:certificate" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">DOT Number</p>
                                <p className="font-medium text-gray-900">{user?.dot || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Yard Information Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#ea580c'}}>
                            <Icon icon="mdi:warehouse" className="text-2xl" style={{color: 'white'}} />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Yard Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:home-city" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Yard Name</p>
                                <p className="font-medium text-gray-900">{user?.yardName || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Icon icon="mdi:map-marker-radius" className="text-xl" style={{color: '#6b7280'}} />
                            <div>
                                <p className="text-sm text-gray-500">Yard Location</p>
                                <p className="font-medium text-gray-900">{user?.yardLocation || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Account Status Card */}
            <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#9333ea'}}>
                        <Icon icon="mdi:account-check" className="text-2xl" style={{color: 'white'}} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Account Status</h3>
                </div>
                
                <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <div className={`w-4 h-4 rounded-full ${user?.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div>
                            <p className="text-sm text-gray-500">Status</p>
                            <p className={`font-medium ${user?.isActive ? 'text-green-500' : 'text-red-500'}`}>{user?.isActive ? 'Active' : 'Inactive'}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <Icon icon="mdi:calendar" className="text-xl" style={{color: '#6b7280'}} />
                        <div>
                            <p className="text-sm text-gray-500">Member Since</p>
                            <p className="font-medium text-gray-900">
                                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                        <Icon icon="mdi:shield-check" className="text-xl" style={{color: '#6b7280'}} />
                        <div>
                            <p className="text-sm text-gray-500">Role</p>
                            <p className="font-medium text-gray-900">{formatRole(user?.role || 'user')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <ChangePasswordModal 
                visible={showChangePasswordModal} 
                setVisible={setShowChangePasswordModal} 
            />
        </div>
    );
}
